import { ColorContext, LayoutSizeContext } from 'App';
import { WidgetVisibilityContext } from 'components/Widgets/WidgetVisibilityContext';
import ConfirmationModal from 'components/Modals/ConfirmationModal';
import { usePerms } from 'contexts/PermsContext';
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Button } from 'react-bootstrap';
import { IconContext } from 'react-icons';
import { FaExpandAlt, FaLock, FaLockOpen, FaTrash } from 'react-icons/fa';
import Popup from 'reactjs-popup';

export default function Widget({
  item,
  index,
  expandable,
  list,
  onListChange,
  removeWidget,
  dashboardPreview = false,
  uninteractable,
  scale,
  lockedFromRole = false,
  gridRowHeight,
}) {
  // Dev-only render tracking
  try {
    if (process.env.NODE_ENV === 'development') {
      require('hooks/useRenderTracker').default('Widget');
    }
  } catch (e) {}

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const widgetShellRef = useRef(null);
  const shouldLazyMountContent = !dashboardPreview;
  const [isWidgetInViewport, setIsWidgetInViewport] = useState(() => !shouldLazyMountContent);
  const [hasWidgetEnteredViewport, setHasWidgetEnteredViewport] = useState(
    () => !shouldLazyMountContent,
  );
  const perms = usePerms();
  const roleLocked = lockedFromRole === true;
  const canOverrideLockedFromRole = perms.testPermission([11, 12, 13], []);

  const layout = useContext(LayoutSizeContext);
  const theme = useContext(ColorContext);
  const widgetIconSize = 10;
  const widgetHeaderFontSize = '0.9375rem';
  const widgetSubtextFontSize = '0.75rem';
  const widgetIconButtonStyle = { padding: 0, lineHeight: 1 };
  const scaleMult = layout?.scaleMult || 1;
  const hasGridRowOverride = Number.isFinite(gridRowHeight) && gridRowHeight > 0;
  const gridVerticalMargin = 10;
  const effectiveSingleHeight = hasGridRowOverride ? gridRowHeight : layout.widgetHeight;
  const effectiveDoubleHeight = hasGridRowOverride
    ? gridRowHeight * 2 + gridVerticalMargin
    : layout.doubleWidgetHeight;
  const getHeightForRowSpan = (rowSpan) => {
    const rows = Math.max(1, Number(rowSpan) || 1);
    if (hasGridRowOverride) {
      return gridRowHeight * rows + gridVerticalMargin * (rows - 1);
    }
    if (rows === 1) return layout.widgetHeight;
    if (rows === 2) return layout.doubleWidgetHeight;
    return layout.widgetHeight * rows + gridVerticalMargin * (rows - 1);
  };
  const effectiveWidgetHeight = getHeightForRowSpan(item?.h);
  const baseDoubleHeight = layout.doubleWidgetHeight / scaleMult;
  const softenedDoubleHeight = baseDoubleHeight * (1 + (scaleMult - 1) * 0.5);
  const shouldMountWidgetContent = !shouldLazyMountContent || hasWidgetEnteredViewport;

  useEffect(() => {
    if (!shouldLazyMountContent) {
      setIsWidgetInViewport(true);
      setHasWidgetEnteredViewport(true);
      return undefined;
    }

    const node = widgetShellRef.current;
    if (!node || typeof window === 'undefined') {
      setIsWidgetInViewport(true);
      setHasWidgetEnteredViewport(true);
      return undefined;
    }

    if (typeof IntersectionObserver === 'undefined') {
      setIsWidgetInViewport(true);
      setHasWidgetEnteredViewport(true);
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries?.[0];
        if (!entry) {
          return;
        }

        const visible = entry.isIntersecting || entry.intersectionRatio > 0;
        setIsWidgetInViewport(visible);
        if (visible) {
          setHasWidgetEnteredViewport(true);
        }
      },
      {
        root: null,
        rootMargin: '320px 0px 320px 0px',
        threshold: 0.01,
      },
    );

    observer.observe(node);

    return () => {
      observer.disconnect();
    };
  }, [shouldLazyMountContent]);

  const widgetVisibilityValue = useMemo(
    () => ({
      isVisible: isWidgetInViewport,
      hasEnteredViewport: hasWidgetEnteredViewport,
      pollingActive: dashboardPreview ? true : isWidgetInViewport,
    }),
    [dashboardPreview, hasWidgetEnteredViewport, isWidgetInViewport],
  );

  return (
    <div
      ref={widgetShellRef}
      className="user-select-none d-flex flex-column p-2 pt-1 rounded-2"
      style={
        dashboardPreview
          ? {
              backgroundColor: theme.base,
              height: `${100}%`,
              width: `${100}%`,
              transform: `scale(${scale})`,
              transformOrigin: 'top left',
              '--widget-header-font-size': widgetHeaderFontSize,
              '--widget-subtext-font-size': widgetSubtextFontSize,
            }
          : {
              background: theme.base,
              height: effectiveWidgetHeight,
              '--widget-header-font-size': widgetHeaderFontSize,
              '--widget-subtext-font-size': widgetSubtextFontSize,
              minHeight:
                item.w === 1 && item.h === 2
                  ? hasGridRowOverride
                    ? effectiveDoubleHeight
                    : softenedDoubleHeight
                  : item.w === 1 && item.h === 1
                    ? hasGridRowOverride
                      ? effectiveSingleHeight
                      : layout.widgetWidth
                    : undefined,
            }
      }
    >
      {uninteractable && (
        <div
          className="position-absolute start-0 end-0 bottom-0"
          style={{ top: 65, zIndex: 1001 }}
        />
      )}
      <div className="d-flex flex-column flex-md-row w-100 justify-content-md-between align-items-center justify-content-center">
        {/* Widget Display Name and Tooltip */}
        <Popup
          trigger={
            // Widget Title
            <div
              className={`${
                item.isDraggable && 'handle'
              } fw-bold overflow-hidden text-truncate text-nowrap`}
              style={{ fontSize: 'var(--widget-header-font-size)' }}
            >
              {item.displayName}
            </div>
          }
          on="hover"
          position="top center"
          closeOnDocumentClick
        >
          {/* Widget Display Name Tooltip */}
          <div
            className="text-white p-2 user-select-none rounded-3"
            style={{ backgroundColor: theme.primary }}
          >
            {item.displayName}
          </div>
        </Popup>
        {/* Widget Controls */}
        <div className="d-flex align-items-center gap-1">
          {/* Lock */}
          {perms.testPermission([8, 9, 10, 11, 12, 13], ['WidgetsAreEditable']) &&
            !uninteractable && (
              <Button
                variant="primary"
                className="btn-transparent p-0"
                disabled={roleLocked && !canOverrideLockedFromRole}
                onClick={() => {
                  if (roleLocked && !canOverrideLockedFromRole) return;
                  const temp = [...list];
                  const current = temp[index] || {};
                  //If locked is undefined, we consider it unlocked, so we set it to true
                  const newLocked = current.locked !== undefined ? !current.locked : true;
                  temp[index] = {
                    ...current,
                    isDraggable: !newLocked,
                    locked: newLocked,
                  };
                  onListChange(temp);
                }}
                style={{
                  ...widgetIconButtonStyle,
                  cursor: roleLocked && !canOverrideLockedFromRole ? 'not-allowed' : undefined,
                  opacity: roleLocked && !canOverrideLockedFromRole ? 0.5 : 1,
                }}
              >
                {item.isDraggable ? (
                  <IconContext.Provider
                    value={{
                      color: theme.fontColor,
                      size: widgetIconSize,
                    }}
                  >
                    <FaLockOpen />
                  </IconContext.Provider>
                ) : (
                  <IconContext.Provider
                    value={{
                      color: theme.fontColor,
                      size: widgetIconSize,
                    }}
                  >
                    <FaLock />
                  </IconContext.Provider>
                )}
              </Button>
            )}
          {!dashboardPreview &&
            !uninteractable &&
            item.sidebarData.href &&
            item.sidebarData.href != '' && (
              <Button
                variant="primary"
                className="btn-transparent p-0"
                style={widgetIconButtonStyle}
                onClick={() => {
                  if (!expandable || !item.sidebarData.href) return;

                  window.open(`${item.sidebarData.href}`, '_blank', 'noreferrer');
                }}
              >
                <IconContext.Provider
                  value={{
                    color: theme.fontColor,
                    size: widgetIconSize,
                  }}
                >
                  <FaExpandAlt />
                </IconContext.Provider>
              </Button>
            )}
          {!dashboardPreview &&
            !uninteractable &&
            perms.testPermission([8, 9, 10, 11, 12, 13], ['WidgetsAreEditable']) &&
            typeof removeWidget === 'function' && (
              <Button
                variant="primary"
                className="btn-transparent p-0"
                disabled={roleLocked && !canOverrideLockedFromRole}
                style={{
                  ...widgetIconButtonStyle,
                  cursor: roleLocked && !canOverrideLockedFromRole ? 'not-allowed' : undefined,
                  opacity: roleLocked && !canOverrideLockedFromRole ? 0.5 : 1,
                }}
                onClick={() => {
                  if (roleLocked && !canOverrideLockedFromRole) return;
                  setShowDeleteConfirm(true);
                }}
              >
                <IconContext.Provider
                  value={{
                    color: theme.fontColor,
                    size: widgetIconSize,
                  }}
                >
                  <FaTrash />
                </IconContext.Provider>
              </Button>
            )}
        </div>
      </div>

      {typeof removeWidget === 'function' && (
        <ConfirmationModal
          show={showDeleteConfirm}
          handleShow={() => setShowDeleteConfirm(false)}
          bodyTxt={`Are you sure you want to delete ${item.displayName || 'this widget'}?`}
          cancelBtnTxt="Cancel"
          confirmBtnTxt="Delete"
          cancelHandler={() => setShowDeleteConfirm(false)}
          confirmHandler={() => {
            removeWidget(index);
            setShowDeleteConfirm(false);
          }}
        />
      )}

      {/* Widget Content */}
      <WidgetVisibilityContext.Provider value={widgetVisibilityValue}>
        <div className="overflow-x-auto w-100 h-100" style={{ minWidth: 0, minHeight: 0 }}>
          {shouldMountWidgetContent ? (
            item.component(item.w, item.h, dashboardPreview)
          ) : (
            <div
              className="w-100 h-100 d-flex align-items-center justify-content-center text-secondary"
              style={{ opacity: 0.8 }}
            >
              Loading widget...
            </div>
          )}
        </div>
      </WidgetVisibilityContext.Provider>
    </div>
  );
}
